import { useDispatch, useSelector } from 'react-redux';
import List from '../../../components/shared/list/list';
import { ListAttendeesPageStyle } from './list-attendees.page.styled';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dashboard from '../../../components/shared/dashboard/dashboard';
import Checkbox from '../../../components/base/checkbox/checkbox';
import { getDateTimeBooking, getStringDateName } from '../../../utils/utils';
import i18n from '../../../translations/strings';
import {
  checkInAttendeeAction,
  checkInAttendeeSuccessAction,
  getListAttendeesAction,
  getReservationAction,
  listAttendeesResetState,
} from '../../../store/customers/list-attendees.store';
import { ListStyled } from '../../../components/shared/list/list.styled';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Colors } from '../../../colors';
import { useModal } from '../../../components/shared/modal-provider/hooks/modal-provider.hook';
import moment from 'moment';
import { ModalVariant } from '../../../components/shared/modal-provider/components/modal/modal.constants';
import CustomChip from '../../../components/shared/custom-chip/custom-chip';
import CsvUploadModal from '../reservation-list/components/csv-upload-modal';
import { CsvUploadModalVariant } from '../reservation-list/components/constants';
import {
  uploadCsvAction,
  uploadCsvResetState,
} from '../../../store/customers/upload-csv.store';
import CustomTooltip from '../../../components/shared/tooltip/tooltip';
import { RadioButtonStyled } from '../../../components/base/radio-button/radio-button.styled';
import { SvgImage, SvgType } from '../../../components/base/svg/svg';

const ListAttendeesPage = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { loading, listAttendees, error, checkInAttendeeSuccess, reservation } =
    useSelector((state: any) => {
      return state.listAttendees;
    });
  const { loadingCsv, errorCsv, uploadCsvSuccess } = useSelector(
    (state: any) => {
      return state.uploadCSV;
    },
  );

  const currentDate = moment(new Date(), 'YYYY-MM-DD HH:mm');

  const [showChip, setShowChip] = useState<any>(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const isEventActive =
    (reservation &&
      reservation.check_in && !reservation.check_out &&
      currentDate.isBetween(
        moment(reservation.start, 'YYYY-MM-DD HH:mm'),
        moment(reservation.end, 'YYYY-MM-DD HH:mm'),
      ))
  const showPresence = reservation && reservation.check_in && !reservation.check_out



  const hasDuplicateName = () => {
    for (let person = 0; person < listAttendees.length - 1; person++) {
      for (
        let attendee = person + 1;
        attendee < listAttendees.length;
        attendee++
      ) {
        if (
          listAttendees[person].full_name === listAttendees[attendee].full_name
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const showWarningDuplicateNames = hasDuplicateName();

  useEffect(() => {
    if (isEventActive) {
      setShowChip(true);
    } else {
      setShowChip(false);
    }
  }, [isEventActive]);

  useEffect(() => {
    dispatch(getReservationAction(params.id));
  }, []);

  useEffect(() => {
    if (checkInAttendeeSuccess) {
      dispatch(checkInAttendeeSuccessAction(false));
      dispatch(
        getListAttendeesAction({
          id: params?.id,
        }),
      );
    }
  }, [checkInAttendeeSuccess]);

  useEffect(() => {
    if (error) {
      openModal({
        message: error,
        variant: ModalVariant.Error,
        handleCloseModal: () => dispatch(listAttendeesResetState()),
      });
    }
  }, [error]);

  useEffect(() => {
    if (errorCsv) {
      openModal({
        message: errorCsv,
        variant: ModalVariant.Error,
      });
    }
    dispatch(uploadCsvResetState());
  }, [errorCsv]);

  useEffect(() => {
    if (uploadCsvSuccess) {
      openModal({
        message: i18n.t('reservations.csvSuccessUpload'),
        variant: ModalVariant.Success,
      });
    }
    dispatch(getListAttendeesAction({ id: params?.id }));
    dispatch(uploadCsvResetState());
  }, [uploadCsvSuccess]);

  const handleCheckboxChange = (item: any) => {
    dispatch(
      checkInAttendeeAction({
        id: item.id,
        check_in: { check_in: !item.checked_in },
      }),
    );
  };

  const uploadCsvFile = (file: File) => {
    dispatch(uploadCsvAction({ file_csv: file, interval: reservation.id }));
  };

  const checkboxCount = useCallback(() => {
    const checkedInAttendees = listAttendees.filter(
      (attendee: any) => attendee.checked_in === true,
    );
    return checkedInAttendees.length;
  }, [listAttendees]);

  const fields = useMemo(() => {
    const commonFields = [
      {
        name: 'name',
        displayName: i18n.t('listAttendees.nameAttendees'),
        component: (item: any) => {
          return <ListStyled.CustomRow>{item.full_name}</ListStyled.CustomRow>;
        },
      },
    ];
    if (showPresence) {
      return [
        ...commonFields,
        {
          name: 'presenza',
          displayName: i18n.t('listAttendees.presence', {
            presence: checkboxCount(),
          }),
          component: (item: any) => {
            return (
              <Checkbox
                checked={item.checked_in}
                onValueChange={() => {
                  handleCheckboxChange(item);
                }}
              />
            );
          },
        },
      ];
    }
    if (reservation && showWarningDuplicateNames && !reservation.check_out) {
      return [
        ...commonFields,
        {
          name: 'duplicated_name',
          customContentDisplayName: true,
          displayName: () => (
            <ListAttendeesPageStyle.ContainerDuplicateAttendees>
              <SvgImage type={SvgType.AlertDuplicateAttendees} />
              <ListAttendeesPageStyle.TextDuplicateAttendees>
                <ListAttendeesPageStyle.TextAttention>
                  {i18n.t('common.attention').toUpperCase() + '! '}
                </ListAttendeesPageStyle.TextAttention>
                {i18n.t('listAttendees.warningDuplicateNames')}
              </ListAttendeesPageStyle.TextDuplicateAttendees>
            </ListAttendeesPageStyle.ContainerDuplicateAttendees>
          ),
          component: (item: any) => {
            return <></>;
          },
        },
      ];
    }
    return commonFields;
  }, [listAttendees, isEventActive, reservation]);

  return (
    <>
      <Dashboard callingPage={'ReservationListPage'}>
        <ListAttendeesPageStyle.Container>
          <ListAttendeesPageStyle.ContainerBack onClick={() => navigate(-1)}>
            <ArrowBackIcon sx={{ color: Colors.White }} fontSize={'small'} />
            <ListAttendeesPageStyle.BackTitle>
              Indietro
            </ListAttendeesPageStyle.BackTitle>
          </ListAttendeesPageStyle.ContainerBack>
          {reservation && (
            <>
              <ListAttendeesPageStyle.TitleContainer>
                <ListAttendeesPageStyle.TitleChipContainer>
                  <ListAttendeesPageStyle.TitlePage>
                    {i18n.t('listAttendees.eventDetailDay', {
                      day: getStringDateName(reservation.start),
                    })}
                  </ListAttendeesPageStyle.TitlePage>
                  {showChip && (
                    <CustomChip label={i18n.t('listAttendees.activeEvent')} />
                  )}
                </ListAttendeesPageStyle.TitleChipContainer>
                {reservation?.has_attendees_list && !reservation.check_in && (
                  <ListAttendeesPageStyle.ContainerUploadCsv
                    onClick={() => setShowCsvModal(true)}
                  >
                    <SvgImage type={SvgType.ReloadCsv} />
                    <ListAttendeesPageStyle.TitleUploadCsv>
                      {`${i18n.t('listAttendees.uploadCsv')}`}
                    </ListAttendeesPageStyle.TitleUploadCsv>
                  </ListAttendeesPageStyle.ContainerUploadCsv>
                )}
              </ListAttendeesPageStyle.TitleContainer>
              <ListAttendeesPageStyle.SubtitlePage>
                {i18n.t('listAttendees.eventDetailTime', {
                  eventStart: getDateTimeBooking(reservation?.start).time,
                  eventEnd: getDateTimeBooking(reservation?.end).time,
                })}
              </ListAttendeesPageStyle.SubtitlePage>
              <ListAttendeesPageStyle.ContainerAttendees>
                <ListAttendeesPageStyle.TitleAttendees>
                  {`${i18n.t('listAttendees.attendeesPresence')}`}
                  <ListAttendeesPageStyle.Attendees>
                    {`${reservation.attendees} ${i18n.t(
                      'listAttendees.attendees',
                    )}`}
                  </ListAttendeesPageStyle.Attendees>
                </ListAttendeesPageStyle.TitleAttendees>
                <ListAttendeesPageStyle.ContainerInfo>
                  <ListAttendeesPageStyle.TitleAttendees>
                    {`${i18n.t('listAttendees.attendeesLoaded')}`}
                    <ListAttendeesPageStyle.Attendees>
                      {`${listAttendees.length} ${i18n.t(
                        'listAttendees.attendees',
                      )}`}
                    </ListAttendeesPageStyle.Attendees>
                  </ListAttendeesPageStyle.TitleAttendees>
                  {listAttendees.length < reservation.attendees &&
                    !isEventActive && (
                      <ListAttendeesPageStyle.ContainerTooltip>
                        <CustomTooltip
                          placement={'right'}
                          title={`${i18n.t('listAttendees.warningCsv')}`}
                          arrow
                        >
                          <RadioButtonStyled.ContainerIconRight>
                            <SvgImage type={SvgType.InfoCsv} />
                          </RadioButtonStyled.ContainerIconRight>
                        </CustomTooltip>
                      </ListAttendeesPageStyle.ContainerTooltip>
                    )}
                </ListAttendeesPageStyle.ContainerInfo>
              </ListAttendeesPageStyle.ContainerAttendees>
              <ListAttendeesPageStyle.ListContainer>
                <List
                  containerComponent={ListAttendeesPageStyle.ListContainer}
                  data={listAttendees}
                  fields={fields}
                  getDataAction={getListAttendeesAction}
                  loading={loading || loadingCsv}
                  statuses={undefined}
                  showMoreButton={false}
                  extraParamsAPi={{ id: params?.id }}
                ></List>
              </ListAttendeesPageStyle.ListContainer>
              <CsvUploadModal
                variant={CsvUploadModalVariant.Edit}
                isVisible={showCsvModal}
                onModalClose={() => setShowCsvModal(false)}
                onCsvUpload={(file: any) => uploadCsvFile(file)}
              />
            </>
          )}
        </ListAttendeesPageStyle.Container>
      </Dashboard>
    </>
  );
};

export default ListAttendeesPage;
